"use client";

import { useEffect, useRef, useState } from "react";
import { parseScore } from "../helpers/formatting";
import type { Game, Teams, EventData } from "../types";

type ScoreFlash = "home" | "away" | null;

/**
 * Owns the live-data lifecycle for a single sports event page:
 *
 * 1. Initial GET /api/sports-live → grab the game/teams/event slot
 *    for this slug. Falls back to /api/sports-live/event/<slug> when
 *    the global state doesn't carry the event (minor leagues / esports
 *    not in the TAG_GAMES overview).
 *    The global teams cache sometimes returns identical placeholder
 *    logos for both home/away — when detected, overlay the real
 *    per-team logos from the event-specific endpoint.
 *
 * 2. Polling fallback: refresh score + period from gamma every 15s
 *    while the game is live. Covers cases where the Sports WS
 *    doesn't push (minor leagues, esports). Fires once at +5s so
 *    the SSE has time to connect first.
 *
 * 3. SSE /api/sports-live/scores/stream while the game is live.
 *    - score updates fire a 2s flash animation on whichever side
 *      changed (home / away)
 *    - price updates set sseLivePrices for the moneyline market
 *    - elapsed/live/status updates patch the game state in place
 */
/**
 * @param slug             Event slug from the URL.
 * @param initialSportsLive Optional SSR-fetched payload from
 *                         `/api/sports-live/event/<slug>` — passed by the
 *                         server component so the first paint already has
 *                         live=true + score + period without waiting for
 *                         the client-side fetch. The hook still owns SSE +
 *                         15s polling for refresh, so freshness is
 *                         preserved on top of the seed.
 */
export function useSportsEventLive(
  slug: string | null | undefined,
  initialSportsLive?: { game?: Game; teams?: Teams; event?: EventData } | null,
) {
  const hasInitial = !!(initialSportsLive?.game);
  const [loading, setLoading] = useState(!hasInitial);
  const [error, setError] = useState<string | null>(null);
  const [game, setGame] = useState<Game | null>(initialSportsLive?.game || null);
  const [teams, setTeams] = useState<Teams | null>(initialSportsLive?.teams || null);
  const [event, setEvent] = useState<EventData | null>(initialSportsLive?.event || null);

  const [sseScore, setSseScore] = useState<string | null>(null);
  const [ssePeriod, setSsePeriod] = useState<string | null>(null);
  const [scoreFlash, setScoreFlash] = useState<ScoreFlash>(null);
  const [sseLivePrices, setSseLivePrices] = useState<(number | null)[] | null>(null);

  const prevScoreRef = useRef<string | null>(null);
  const gameIdToSlugRef = useRef<Record<string, string>>({});
  const seededFromSSR = useRef(hasInitial);

  // 1) Initial fetch — global + per-event in parallel.
  //
  // The previous flow was serial: await /api/sports-live, then if the slug
  // wasn't in `games[slug]`, await /api/sports-live/event/<slug>. For
  // events outside TAG_GAMES (e.g. la-liga BAR vs RMA on 2026-05-10),
  // the user paid both round-trips end-to-end (~1.5-3s cold) before the
  // page could render with `live: true`.
  //
  // Parallel: fire both, prefer the global when it carries the slug, fall
  // back to per-event when it doesn't, and always finish with the global's
  // gameIdToSlug map so SSE can route updates. Total cold latency is now
  // max(global, per-event), not sum.
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;

    const fetchGameData = async () => {
      // SSR-seeded path: keep showing the seeded data, refresh in
      // background. Only flip loading=true when we have no seed and
      // need a full client-side fetch.
      if (!seededFromSSR.current) {
        setLoading(true);
      }
      setError(null);

      const globalPromise = fetch("/api/sports-live")
        .then(res => res.ok ? res.json() : null)
        .catch(() => null);
      const eventPromise = fetch(`/api/sports-live/event/${encodeURIComponent(slug)}`)
        .then(res => res.ok ? res.json() : null)
        .catch(() => null);

      const [globalData, eventData] = await Promise.all([globalPromise, eventPromise]);
      if (cancelled) return;

      const globalState = globalData?.success ? globalData.state : null;
      const globalGames = globalState?.games as Record<string, Game> | undefined;
      const globalTeams = globalState?.teams as Record<string, Teams> | undefined;
      const globalEvents = globalState?.events as Record<string, EventData> | undefined;
      const globalIdMap = globalState?.gameIdToSlug as Record<string, string> | undefined;

      // Always pick up the gameIdToSlug map when the global resolves —
      // SSE updates depend on it regardless of which payload provides
      // the initial game state.
      if (globalIdMap) gameIdToSlugRef.current = globalIdMap;

      const inGlobal = !!(globalGames && globalGames[slug]);
      const eventOk = !!(eventData?.success && eventData.game);

      if (inGlobal && globalGames && globalTeams && globalEvents) {
        setGame(globalGames[slug]);
        setTeams(globalTeams[slug]);
        setEvent(globalEvents[slug]);
        // Logo placeholder overlay — same fix-up as before, but we
        // already have the per-event payload in hand from the parallel
        // fetch, so no extra round-trip.
        const tIn = globalTeams[slug];
        const placeholdersDetected = !!tIn && (
          (!tIn.home?.logo || !tIn.away?.logo)
          || (tIn.home?.logo && tIn.home.logo === tIn.away?.logo)
        );
        if (placeholdersDetected && eventOk && eventData.teams) {
          setTeams((prev) => {
            if (!prev) return eventData.teams;
            return {
              home: {
                ...prev.home,
                logo: eventData.teams.home?.logo || prev.home?.logo,
                abbreviation: eventData.teams.home?.abbreviation || prev.home?.abbreviation,
                alias: eventData.teams.home?.alias || prev.home?.alias,
              },
              away: {
                ...prev.away,
                logo: eventData.teams.away?.logo || prev.away?.logo,
                abbreviation: eventData.teams.away?.abbreviation || prev.away?.abbreviation,
                alias: eventData.teams.away?.alias || prev.away?.alias,
              },
            } as Teams;
          });
        }
      } else if (eventOk) {
        // Slug not in TAG_GAMES overview — use the per-event payload.
        setGame(eventData.game);
        setTeams(eventData.teams);
        setEvent(eventData.event);
      } else {
        setError("Game not found");
      }
      setLoading(false);
    };

    fetchGameData();
    return () => { cancelled = true; };
  }, [slug]);

  // 2) Polling fallback for live games where SSE may not push
  useEffect(() => {
    if (!game?.live || !slug) return;

    const poll = async () => {
      try {
        const res = await fetch(`/api/sports-live/event/${encodeURIComponent(slug)}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!data.success) return;
        if (data.game?.score && data.game.score !== (sseScore || game?.score)) {
          setSseScore(data.game.score);
        }
        if (data.game?.period) setSsePeriod(data.game.period);
        if (data.game?.ended) setGame(prev => prev ? { ...prev, ended: true, live: false } : prev);
      } catch { /* ignore */ }
    };

    const interval = setInterval(poll, 15000);
    // Also poll once immediately after a short delay (gives SSE time to connect first)
    const initialTimeout = setTimeout(poll, 5000);

    return () => { clearInterval(interval); clearTimeout(initialTimeout); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.live, slug]);

  // 3) SSE for live scores + prices (with score-change flash animation)
  useEffect(() => {
    if (!game?.live || !slug) return;

    const es = new EventSource("/api/sports-live/scores/stream");
    es.onmessage = (e) => {
      try {
        const updates = JSON.parse(e.data);

        // Price update from SportsPriceWs — keyed by game slug
        if (updates?.type === 'price' && updates.prices?.[slug]) {
          setSseLivePrices(updates.prices[slug]);
          return;
        }

        const processUpdate = (u: any) => {
          // SSE sends gameId, map it to slug
          const updateSlug = u.slug || gameIdToSlugRef.current[u.gameId];
          if (updateSlug !== slug) return;

          // Update score with flash animation
          if (u.score) {
            if (prevScoreRef.current && prevScoreRef.current !== u.score) {
              const oldParsed = parseScore(prevScoreRef.current);
              const newParsed = parseScore(u.score);
              if (newParsed.home !== oldParsed.home) {
                setScoreFlash("home");
                setTimeout(() => setScoreFlash(null), 2000);
              } else if (newParsed.away !== oldParsed.away) {
                setScoreFlash("away");
                setTimeout(() => setScoreFlash(null), 2000);
              }
            }
            prevScoreRef.current = u.score;
            setSseScore(u.score);
          }

          // Update period, elapsed, and game status from SSE
          if (u.period) setSsePeriod(u.period);
          if (u.elapsed !== undefined || u.live !== undefined || u.status) {
            setGame(prev => prev ? {
              ...prev,
              ...(u.elapsed !== undefined && { elapsed: u.elapsed }),
              ...(u.live !== undefined && { live: u.live }),
              ...(u.status === 'finished' && { ended: true, live: false }),
            } : prev);
          }
        };

        if (Array.isArray(updates)) {
          for (const u of updates) processUpdate(u);
        } else {
          processUpdate(updates);
        }
      } catch { /* ignore */ }
    };

    return () => es.close();
  }, [game?.live, slug]);

  return {
    loading,
    error,
    game,
    setGame,
    teams,
    event,
    sseScore,
    ssePeriod,
    scoreFlash,
    sseLivePrices,
  };
}
